import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { DollarSign, CheckCircle } from 'lucide-react';

export default function Onboarding() {
  const { setupPayment, user } = useAuth();
  const [paymentMethod, setPaymentMethod] = useState('venmo');
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!username.trim()) {
      toast.error('Please enter your username');
      return;
    }
    
    setLoading(true);
    
    try {
      await setupPayment(paymentMethod, username);
      toast.success('Payment setup complete!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Setup failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-darker flex flex-col">
      <div className="flex-1 flex flex-col p-6">
        {/* Progress indicator */}
        <div className="flex items-center gap-2 mb-8">
          <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center">
            <CheckCircle className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 h-1 bg-emerald-500 rounded" />
          <div className="w-8 h-8 rounded-full bg-[#FF5F00] flex items-center justify-center">
            <span className="text-white font-bold">2</span>
          </div>
        </div>
        
        <h1 className="font-heading text-3xl font-bold text-white mb-2">
          How do you get paid?
        </h1>
        <p className="text-slate-400 mb-8">
          Choose your payment method so customers can pay you instantly.
        </p>
        
        {/* Payment Method Selection */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <button
            type="button"
            data-testid="payment-venmo"
            onClick={() => setPaymentMethod('venmo')}
            className={`p-6 rounded-xl border-2 transition-colors ${
              paymentMethod === 'venmo'
                ? 'border-[#FF5F00] bg-slate-dark'
                : 'border-slate-light bg-slate-darker hover:border-slate-400'
            }`}
          >
            <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center mb-3 mx-auto">
              <span className="text-white font-bold text-xl">V</span>
            </div>
            <span className={`font-bold ${paymentMethod === 'venmo' ? 'text-white' : 'text-slate-400'}`}>
              Venmo
            </span>
          </button>
          
          <button
            type="button"
            data-testid="payment-cashapp"
            onClick={() => setPaymentMethod('cashapp')}
            className={`p-6 rounded-xl border-2 transition-colors ${
              paymentMethod === 'cashapp'
                ? 'border-[#FF5F00] bg-slate-dark'
                : 'border-slate-light bg-slate-darker hover:border-slate-400'
            }`}
          >
            <div className="w-12 h-12 bg-emerald-500 rounded-lg flex items-center justify-center mb-3 mx-auto">
              <DollarSign className="w-6 h-6 text-white" />
            </div>
            <span className={`font-bold ${paymentMethod === 'cashapp' ? 'text-white' : 'text-slate-400'}`}>
              Cash App
            </span>
          </button>
        </div>
        
        {/* Username Input */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="username" className="text-slate-300 uppercase text-xs tracking-wider font-bold">
              {paymentMethod === 'venmo' ? 'Venmo Username' : 'Cash App $Cashtag'}
            </Label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-lg font-bold">
                {paymentMethod === 'cashapp' ? '$' : '@'}
              </span>
              <Input
                id="username"
                data-testid="payment-username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder={paymentMethod === 'venmo' ? 'johnsmith' : 'JohnSmith'}
                className="h-14 pl-12 bg-slate-dark border-2 border-slate-light text-white placeholder:text-slate-500 text-lg rounded-lg"
                required
              />
            </div>
            <p className="text-slate-500 text-sm">
              This is how customers will send you payments
            </p>
          </div>
          
          <Button
            type="submit"
            data-testid="complete-setup"
            disabled={loading}
            className="w-full h-14 bg-[#FF5F00] hover:bg-orange-hover text-white font-bold uppercase tracking-wide text-lg rounded-lg shadow-hard active:translate-y-0.5 transition-transform"
          >
            {loading ? 'Setting up...' : 'Complete Setup'}
          </Button>
        </form>
        
        <p className="mt-6 text-slate-500 text-sm text-center">
          You can change this anytime in Settings
        </p>
      </div>
    </div>
  );
}
